/**
 * セッションとチャット履歴の統合管理フック
 * App.tsx と WidgetApp.tsx の共通ロジックを抽出
 *
 * セッション状態はReact stateで管理し、localStorageは使用しない。
 * ページリフレッシュ時はバックエンドからセッション一覧を取得し、
 * 最新のセッションを自動選択する。
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { AgeGroup, SessionInfo } from '../types';
import { SessionInitStatus } from '../types';
import {
  createSession,
  getChatHistory,
  deleteSession,
  prefetchUsageLimits,
  ensureCsrfToken,
} from '../services/api';

interface UseSessionManagementOptions {
  /** 初期化後にチャット履歴をロードするか */
  loadHistoryOnInit?: boolean;
}

interface UseSessionManagementReturn {
  // 状態
  sessionId: string | null;
  ageGroup: AgeGroup | null;
  userId: string | null;
  isLoading: boolean;
  initStatus: SessionInitStatus;
  chatHistory: SessionInfo[];
  isCreatingSession: boolean;
  deletingSessionIds: Set<string>;

  // ハンドラー
  loadChatHistory: () => Promise<void>;
  handleNewChat: () => Promise<void>;
  handleSelectChat: (sessionId: string) => void;
  handleDeleteSession: (sessionIdToDelete: string) => Promise<void>;
  setChatHistory: React.Dispatch<React.SetStateAction<SessionInfo[]>>;
}

export function useSessionManagement(
  options: UseSessionManagementOptions = {}
): UseSessionManagementReturn {
  const { loadHistoryOnInit = true } = options;
  const { i18n } = useTranslation();

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [ageGroup, setAgeGroup] = useState<AgeGroup | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [initStatus, setInitStatus] = useState<SessionInitStatus>(SessionInitStatus.LOADING);
  const [chatHistory, setChatHistory] = useState<SessionInfo[]>([]);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [deletingSessionIds, setDeletingSessionIds] = useState<Set<string>>(new Set());

  // チャット履歴をロード
  const loadChatHistory = useCallback(async () => {
    const { sessions } = await getChatHistory();
    setChatHistory(sessions);
  }, []);

  // 既存のセッションをチェック & 初期化
  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        void prefetchUsageLimits();
        const { sessions, user, unauthenticated, paidPlanRequired } = await getChatHistory();
        if (cancelled) return;

        if (unauthenticated || paidPlanRequired) {
          setSessionId(null);
          setAgeGroup(null);
          setUserId(null);
          setChatHistory([]);
          setInitStatus(
            unauthenticated
              ? SessionInitStatus.UNAUTHENTICATED
              : SessionInitStatus.PAID_PLAN_REQUIRED
          );
          setIsLoading(false);
          return;
        }

        // 認証が通ったのでCSRFトークンを確保する。
        // 発行経路は createSession() だけで、既存セッションがあると呼ばれない。
        // ここで確保しないと、そのタブはトークンを持たないまま操作が進む。
        await ensureCsrfToken();
        if (cancelled) return;

        if (user && !localStorage.getItem('language')) {
          await i18n.changeLanguage(user.recommended_language);
          if (cancelled) return;
        }

        setChatHistory(sessions);

        if (sessions.length > 0) {
          // 最新のセッションを選択（セッションは最新順にソートされている想定）
          const latestSession = sessions[0];
          if (cancelled) return;
          setSessionId(latestSession.session_id);
          setAgeGroup(latestSession.age_group);
          setUserId(latestSession.user_id);
        } else {
          const session = await createSession();
          if (cancelled) return;
          setSessionId(session.session_id);
          setAgeGroup(session.age_group);
          setUserId(session.user_id);
          if (loadHistoryOnInit) {
            await loadChatHistory();
          }
        }
        if (cancelled) return;
        setInitStatus(SessionInitStatus.READY);
      } catch (error) {
        console.error('Session initialization failed', error);
        if (cancelled) return;
        setInitStatus(SessionInitStatus.ERROR);
      }
      if (cancelled) return;
      setIsLoading(false);
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, [loadHistoryOnInit, loadChatHistory, i18n]);

  // 新しいチャットを開始
  const handleNewChat = useCallback(async () => {
    if (!ageGroup || isCreatingSession) return;

    setIsCreatingSession(true);
    try {
      // セッション一覧を再取得して最新の状態を確認
      const { sessions } = await getChatHistory();
      setChatHistory(sessions);

      // 既存の空セッション（message_count が 0 または未定義）を探す
      // APIから文字列 "0" として返ってくる場合があるため、Number()で変換して比較
      const emptySession = sessions.find((s) => Number(s.message_count) === 0);
      if (emptySession) {
        // 空のセッションがあればそれを選択
        setSessionId(emptySession.session_id);
        setAgeGroup(emptySession.age_group);
        return;
      }

      const session = await createSession();

      // 新しいセッションを即座にチャット履歴に追加（楽観的更新）
      const now = Date.now() / 1000;
      const newSession: SessionInfo = {
        session_id: session.session_id,
        user_id: session.user_id,
        age_group: session.age_group,
        start_time: now,
        last_activity: now,
        message_count: 0,
      };
      setChatHistory((prev) => [newSession, ...prev]);

      setSessionId(session.session_id);
      setAgeGroup(session.age_group);
    } catch {
      // New session creation failed - user can retry
    } finally {
      setIsCreatingSession(false);
    }
  }, [ageGroup, isCreatingSession]);

  // チャットを選択
  const handleSelectChat = useCallback(
    (selectedSessionId: string) => {
      const selectedSession = chatHistory.find((s) => s.session_id === selectedSessionId);
      if (selectedSession) {
        setSessionId(selectedSessionId);
        setAgeGroup(selectedSession.age_group);
      }
    },
    [chatHistory]
  );

  // セッションを削除
  const handleDeleteSession = useCallback(
    async (sessionIdToDelete: string) => {
      // 削除中状態を設定
      setDeletingSessionIds((prev) => new Set(prev).add(sessionIdToDelete));

      try {
        const result = await deleteSession(sessionIdToDelete);
        if (result.deleted) {
          // 削除成功または既に削除済み（404）の場合

          // 404の場合は即座にチャット履歴を再取得して最新の状態に同期
          if (result.notFound) {
            await loadChatHistory();
          } else {
            // 通常の削除成功時は楽観的更新
            const updatedHistory = chatHistory.filter((s) => s.session_id !== sessionIdToDelete);
            setChatHistory(updatedHistory);
          }

          // もし削除したセッションが現在選択中のセッションなら
          const currentHistory = chatHistory.filter((s) => s.session_id !== sessionIdToDelete);

          if (sessionId === sessionIdToDelete) {
            if (currentHistory.length > 0) {
              // 他のセッションがある場合は最新のセッションを選択
              const latestSession = currentHistory[0];
              setSessionId(latestSession.session_id);
              setAgeGroup(latestSession.age_group);
            } else {
              // セッションが0個になった場合のみ新しいセッションを作成
              await handleNewChat();
            }
          }

          // チャット履歴を再ロード（バックエンドと同期）
          await loadChatHistory();
        }
      } finally {
        // 削除中状態を解除
        setDeletingSessionIds((prev) => {
          const next = new Set(prev);
          next.delete(sessionIdToDelete);
          return next;
        });
      }
    },
    [chatHistory, sessionId, loadChatHistory, handleNewChat]
  );

  return {
    sessionId,
    ageGroup,
    userId,
    isLoading,
    initStatus,
    chatHistory,
    isCreatingSession,
    deletingSessionIds,
    loadChatHistory,
    handleNewChat,
    handleSelectChat,
    handleDeleteSession,
    setChatHistory,
  };
}
